import type { DNRDatabase } from '@data/db/database';
import type { Document, DocumentLine } from '@data/db/types';
import { resolvePlaceOfSupply } from '@core/gst/split';
import { asQuantity } from '@core/money';
import {
  getLineAvailability,
  type FulfilmentLink,
  type LinkRelation,
} from '@domain/fulfilment';


import { nextVersion, recordBusinessMutations } from '@data/sync/recordBusinessMutation';
import { issueDocument, type IssueLineInput, type IssueParams } from './documentService';
import { listRecentDocuments, loadDocument, type RecentDocumentsPage } from './documentReader';
import { loadBusinessIdentityWithImages } from './businessReader';
import { resolveActiveBusiness } from './businessSession';
import { currentDeviceId as getDeviceId } from '@data/sync/deviceIdentity';
import { ulid } from '../../platform/ids';

const USER = { id: 'local', name: 'You' };

export class DeliveryNoteServiceError extends Error {
  constructor(
    readonly code:
      | 'NO_BUSINESS'
      | 'SOURCE_NOT_FOUND'
      | 'SOURCE_NOT_ISSUED'
      | 'WRONG_BUSINESS'
      | 'NO_DELIVERABLE_LINES'
      | 'INVALID_LINES'
      | 'NOT_FOUND'
      | 'NOT_EDITABLE'
      | 'SOURCE_LINE_INVALID'
      | 'INVALID_PLACE_OF_SUPPLY'
      | 'STOCK_LOCATION_REQUIRED'
      | 'HAS_CHILDREN',
    message: string,
  ) {
    super(message);
    this.name = 'DeliveryNoteServiceError';
  }
}

export interface DeliveryNoteLineInput {
  readonly sourceLineId: string;
  readonly quantityMilli: number;
  readonly freeQuantityMilli?: number;
}

export interface DeliveryNoteInput {
  readonly salesOrderId: string;
  readonly docDate: string;
  readonly lines: readonly DeliveryNoteLineInput[];
  readonly now?: string;
  readonly deviceId?: string;
}

export interface DeliverySourceLine extends DocumentLine {
  readonly deliverableQtyMilli: number;
  readonly productName: string;
  readonly isStockTracked: boolean;
  readonly defaultStockLocationId?: string;
}

export interface DeliverySource {
  readonly order: Document;
  readonly lines: readonly DeliverySourceLine[];
}

function assertDate(value: string): void {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
    throw new DeliveryNoteServiceError('INVALID_LINES', 'Enter a valid delivery note date.');
  }
}

function checkSourceState(order: Document, businessStateCode: string): void {
  const hasAnyState = Boolean(
    order.placeOfSupplyCode || order.deliveryStateCode || order.partySnapshot.stateCode,
  );
  if (!hasAnyState) return;
  const resolved = resolvePlaceOfSupply({
    businessStateCode,
    customerStateCode: order.partySnapshot.stateCode,
    deliveryStateCode: order.deliveryStateCode,
    overrideStateCode: order.placeOfSupplyCode,
  });
  if (!resolved.resolved) {
    throw new DeliveryNoteServiceError(
      'INVALID_PLACE_OF_SUPPLY',
      `The source order has an invalid place-of-supply state (${resolved.reason}). Correct the source transaction before dispatch.`,
    );
  }
}

async function readSource(
  db: DNRDatabase,
  salesOrderId: string,
  businessId: string,
  excludeDeliveryNoteId?: string,
): Promise<DeliverySource> {
  const order = await db.documents.get(salesOrderId);
  if (!order || order.docType !== 'SALES_ORDER') {
    throw new DeliveryNoteServiceError('SOURCE_NOT_FOUND', 'The selected sales order could not be found.');
  }
  if (order.businessId !== businessId) {
    throw new DeliveryNoteServiceError('WRONG_BUSINESS', 'The sales order belongs to another business.');
  }
  if (order.status !== 'ISSUED') {
    throw new DeliveryNoteServiceError('SOURCE_NOT_ISSUED', 'Issue the sales order before creating a delivery note.');
  }
  const business = await db.businesses.get(businessId);
  if (!business) throw new DeliveryNoteServiceError('NO_BUSINESS', 'Select a business before creating a delivery note.');
  checkSourceState(order, business.stateCode);

  const [sourceLines, links, stockLocations] = await Promise.all([
    db.documentLines.where('[documentId+lineNo]').between([order.id, -Infinity], [order.id, Infinity]).toArray(),
    db.documentLinks.where('toDocumentId').equals(order.id).toArray(),
    db.stockLocations.where('businessId').equals(businessId).toArray(),
  ]);
  const defaultLocation = stockLocations.find((location) => location.isDefault) ?? stockLocations[0];
  const lines: DeliverySourceLine[] = [];
  for (const line of sourceLines) {
    const product = line.productId ? await db.products.get(line.productId) : undefined;
    const lineLinks = links
      .filter((link) => link.toLineId === line.id && link.fromDocumentId !== excludeDeliveryNoteId)
      .map((link) => ({ relation: link.relation, qty: asQuantity(link.qtyMilli), fromDocType: link.fromDocType }));
    const availability = getLineAvailability(
      {
        lineId: line.id,
        quantity: asQuantity(line.quantityMilli + line.freeQuantityMilli),
        fulfilledQty: asQuantity(line.fulfilledQtyMilli),
        invoicedQty: asQuantity(line.invoicedQtyMilli),
      },
      lineLinks,
      { allowInvoiceWithoutDelivery: business.documentSettings.allowInvoiceWithoutDelivery },
    );
    lines.push({
      ...line,
      deliverableQtyMilli: Number(availability.deliverableQty),
      productName: line.description || product?.name || 'Item',
      isStockTracked: product?.isStockTracked ?? false,
      ...(line.stockLocationId || !product?.isStockTracked || !defaultLocation
        ? {}
        : { defaultStockLocationId: defaultLocation.id }),
    });
  }
  return { order, lines };
}

export async function loadSalesOrderForDelivery(
  db: DNRDatabase,
  salesOrderId: string,
  options: { businessId?: string; excludeDeliveryNoteId?: string } = {},
): Promise<DeliverySource> {
  const businessId = options.businessId ?? (await resolveActiveBusiness(db)).id;
  return readSource(db, salesOrderId, businessId, options.excludeDeliveryNoteId);
}

function dateToday(): string {
  return new Date().toISOString().slice(0, 10);
}

async function buildDeliveryNoteDraft(db: DNRDatabase, input: DeliveryNoteInput, identity?: Document) {
  assertDate(input.docDate);
  if (!input.lines.length) {
    throw new DeliveryNoteServiceError('NO_DELIVERABLE_LINES', 'Choose at least one available sales order line to dispatch.');
  }
  const sourceLineIds = input.lines.map((line) => line.sourceLineId);
  if (new Set(sourceLineIds).size !== sourceLineIds.length) {
    throw new DeliveryNoteServiceError('INVALID_LINES', 'A sales order line can appear only once on a delivery note.');
  }

  const businessId = identity?.businessId ?? (await resolveActiveBusiness(db)).id;
  const existing = identity ? await db.documents.get(identity.id) : undefined;
  if (identity && (!existing || existing.docType !== 'DELIVERY_NOTE' || existing.status !== 'DRAFT')) {
    throw new DeliveryNoteServiceError('NOT_EDITABLE', 'Only a delivery note draft can be edited.');
  }
  if (identity && existing?.businessId !== businessId) {
    throw new DeliveryNoteServiceError('WRONG_BUSINESS', 'The delivery note belongs to another business.');
  }
  const salesOrderId = existing?.salesOrderId ?? input.salesOrderId;
  if (!salesOrderId || (existing?.salesOrderId && existing.salesOrderId !== input.salesOrderId)) {
    throw new DeliveryNoteServiceError('SOURCE_LINE_INVALID', 'A delivery note cannot be reassigned to another sales order.');
  }
  const source = await readSource(db, salesOrderId, businessId, existing?.id);
  const sourceLinesById = new Map(source.lines.map((line) => [line.id, line]));
  const business = await db.businesses.get(businessId);
  if (!business) throw new DeliveryNoteServiceError('NO_BUSINESS', 'Select a business before creating a delivery note.');
  const resolvedLines: IssueLineInput[] = input.lines.map((line) => {
    const sourceLine = sourceLinesById.get(line.sourceLineId);
    if (!sourceLine || sourceLine.documentId !== salesOrderId || !sourceLine.productId) {
      throw new DeliveryNoteServiceError('SOURCE_LINE_INVALID', 'A delivery line must come from the selected sales order.');
    }
    const totalQuantity = line.quantityMilli + (line.freeQuantityMilli ?? 0);
    if (!Number.isSafeInteger(line.quantityMilli) || line.quantityMilli <= 0 || !Number.isSafeInteger(line.freeQuantityMilli ?? 0) || (line.freeQuantityMilli ?? 0) < 0 || totalQuantity > sourceLine.deliverableQtyMilli) {
      throw new DeliveryNoteServiceError('INVALID_LINES', `Requested quantity exceeds the ${sourceLine.deliverableQtyMilli / 1000} available for ${sourceLine.description}.`);
    }
    const stockLocationId = sourceLine.stockLocationId ?? sourceLine.defaultStockLocationId;
    if (sourceLine.isStockTracked && !stockLocationId) {
      throw new DeliveryNoteServiceError('STOCK_LOCATION_REQUIRED', `Choose a stock location for ${sourceLine.description} before dispatch.`);
    }
    return {
      productId: sourceLine.productId,
      ...(stockLocationId ? { stockLocationId } : {}),
      description: sourceLine.description,
      descriptionExtra: sourceLine.descriptionExtra,
      hsnSac: sourceLine.hsnSac,
      unit: sourceLine.unit,
      quantityMilli: line.quantityMilli,
      freeQuantityMilli: line.freeQuantityMilli ?? 0,
      ratePaise: sourceLine.ratePaise,
      discountType: sourceLine.discountType,
      discountValue: sourceLine.discountValue,
      taxRateBp: sourceLine.taxRateBp,
      cessRateBp: sourceLine.cessRateBp,
      sourceLineId: sourceLine.id,
      isStockTracked: sourceLine.isStockTracked,
    };
  });

  const deviceId = input.deviceId ?? (await getDeviceId(db));
  const params: IssueParams = {
    businessId,
    businessStateCode: business.stateCode,
    docType: 'DELIVERY_NOTE',
    docDate: input.docDate,
    customerId: source.order.customerId,
    partySnapshot: { ...source.order.partySnapshot, addressLines: [...source.order.partySnapshot.addressLines] },
    placeOfSupplyCode: source.order.placeOfSupplyCode,
    deliveryAddressLines: source.order.deliveryAddressLines ? [...source.order.deliveryAddressLines] : undefined,
    deliveryStateCode: source.order.deliveryStateCode,
    deliveryStateName: source.order.deliveryStateName,
    salesOrderId,
    lines: resolvedLines,
    notes: source.order.notes,
    terms: source.order.terms,
    roundOff: false,
    status: 'DRAFT',
    ...(existing ? { replaceIdentity: { id: existing.id, docNumber: existing.docNumber } } : {}),
    user: USER,
    now: input.now ?? new Date().toISOString(),
    deviceId,
    fyStartMonth: business.fyStartMonth,
  };

  const result = await db.transaction(
    'rw',
    [db.documents, db.documentLines, db.documentLinks, db.stockTransactions, db.documentSequences, db.auditLogs, db.parties, db.businesses, db.syncOutbox],
    async () => {
      const currentOrder = await db.documents.get(salesOrderId);
      if (!currentOrder || currentOrder.docType !== 'SALES_ORDER' || currentOrder.status !== 'ISSUED' || currentOrder.businessId !== businessId) {
        throw new DeliveryNoteServiceError('SOURCE_NOT_ISSUED', 'The source sales order must remain issued and belong to this business.');
      }
      return issueDocument(db as never, params);
    },
  );
  return { id: result.id, docNumber: result.docNumber, grandTotalPaise: result.grandTotalPaise };
}

export async function createDeliveryNoteDraft(db: DNRDatabase, input: DeliveryNoteInput) {
  return buildDeliveryNoteDraft(db, input);
}

export async function convertSalesOrderToDeliveryNoteDraft(
  db: DNRDatabase,
  input: { salesOrderId: string; docDate?: string; now?: string; deviceId?: string },
) {
  const businessId = (await resolveActiveBusiness(db)).id;
  const source = await readSource(db, input.salesOrderId, businessId);
  const lines = source.lines
    .filter((line) => line.deliverableQtyMilli > 0)
    .map((line) => ({ sourceLineId: line.id, quantityMilli: line.deliverableQtyMilli }));
  if (!lines.length) {
    throw new DeliveryNoteServiceError('NO_DELIVERABLE_LINES', 'All quantities on this sales order have already been dispatched.');
  }
  return buildDeliveryNoteDraft(db, {
    salesOrderId: input.salesOrderId,
    docDate: input.docDate ?? dateToday(),
    lines,
    now: input.now,
    deviceId: input.deviceId,
  });
}

export async function updateDeliveryNoteDraft(
  db: DNRDatabase,
  id: string,
  input: Omit<DeliveryNoteInput, 'salesOrderId'>,
) {
  const existing = await db.documents.get(id);
  if (!existing || existing.docType !== 'DELIVERY_NOTE') {
    throw new DeliveryNoteServiceError('NOT_FOUND', 'Delivery note not found.');
  }
  if (existing.status !== 'DRAFT') {
    throw new DeliveryNoteServiceError('NOT_EDITABLE', 'Issued delivery notes are immutable.');
  }
  return buildDeliveryNoteDraft(db, { ...input, salesOrderId: existing.salesOrderId ?? '' }, existing);
}

export async function deleteDeliveryNoteDraft(
  db: DNRDatabase,
  id: string,
  input: { now?: string; deviceId?: string } = {},
) {
  const now = input.now ?? new Date().toISOString();
  const deviceId = input.deviceId ?? (await getDeviceId(db));
  return db.transaction('rw', [db.documents, db.documentLines, db.documentLinks, db.auditLogs, db.syncOutbox], async () => {
    const note = await db.documents.get(id);
    if (!note || note.docType !== 'DELIVERY_NOTE') throw new DeliveryNoteServiceError('NOT_FOUND', 'Delivery note not found.');
    if (note.status !== 'DRAFT') throw new DeliveryNoteServiceError('NOT_EDITABLE', 'Only a delivery note draft can be deleted.');
    const children = await db.documentLinks.where('toDocumentId').equals(id).toArray();
    if (children.some((link) => link.fromDocumentId !== id)) {
      throw new DeliveryNoteServiceError('HAS_CHILDREN', 'This delivery note is referenced by another document and cannot be deleted.');
    }
    const lines = await db.documentLines.where('documentId').equals(id).toArray();
    const links = await db.documentLinks.where('fromDocumentId').equals(id).toArray();
    await db.documentLines.where('documentId').equals(id).delete();
    await db.documentLinks.where('fromDocumentId').equals(id).delete();
    await db.documents.delete(id);
    await db.auditLogs.add({
      id: ulid(), businessId: note.businessId, entityType: 'DOCUMENT', entityId: id,
      action: 'DELETE', before: JSON.stringify({ docNumber: note.docNumber, status: note.status }),
      userId: USER.id, userName: USER.name, occurredAt: now,
    });
    await recordBusinessMutations(db as never, [
      { businessId: note.businessId, deviceId, entityType: 'document', entityId: id, operation: 'DELETE', version: nextVersion(note.syncVersion), changedAt: now, deletedAt: now },
      ...lines.map((line) => ({ businessId: line.businessId, deviceId, entityType: 'documentLine' as const, entityId: line.id, operation: 'DELETE' as const, version: nextVersion(line.syncVersion ?? 1), changedAt: now, deletedAt: now })),
      ...links.map((link) => ({ businessId: link.businessId, deviceId, entityType: 'documentLink' as const, entityId: link.id, operation: 'DELETE' as const, version: nextVersion(link.syncVersion ?? 1), changedAt: now, deletedAt: now })),
    ]);
    return { id };
  });
}

/**
 * The issued sales orders that still have SOMETHING left to dispatch.
 *
 * WHY THIS EXISTS RATHER THAN FILTERING IN THE SCREEN
 * --------------------------------------------------
 * `listRecentDocuments` knows about documents, not about fulfilment. A sales order
 * stays ISSUED forever after it is issued, so "issued orders" is not the same set
 * as "orders with goods still to send". Offering the second list verbatim would
 * put a "Create delivery note" link on orders whose every quantity has already
 * left the shop, and following it lands the user on a refusal.
 *
 * The test is the ARCHITECTURE'S OWN, not a counter of our own: an order line is
 * exhausted when the `DELIVERED_AGAINST` links into it account for the whole
 * ordered quantity. That is exactly the rule `getLineAvailability` applies, so a
 * note cannot be offered here that the service would then reject.
 */
export async function listDispatchableSalesOrders(
  db: DNRDatabase,
  businessId: string,
): Promise<Document[]> {
  const business = await db.businesses.get(businessId);
  if (!business) {
    throw new DeliveryNoteServiceError('NO_BUSINESS', 'Select a business before creating a delivery note.');
  }
  const page = await listRecentDocuments(db as never, businessId, 200, true, 'SALES_ORDER');
  const orders = page.documents.filter((document) => document.status === 'ISSUED');

  const dispatchable: Document[] = [];
  for (const order of orders) {
    const lines = await db.documentLines.where('documentId').equals(order.id).toArray();
    if (!lines.length) continue;
    const links = await db.documentLinks.where('toDocumentId').equals(order.id).toArray();
    const linksByLineId = new Map<string, FulfilmentLink[]>();
    for (const link of links) {
      const key = link.toLineId ?? '';
      const bucket = linksByLineId.get(key) ?? [];
      bucket.push({
        relation: link.relation as LinkRelation,
        qty: asQuantity(link.qtyMilli),
        fromDocType: link.fromDocType,
      });
      linksByLineId.set(key, bucket);
    }
    const anythingLeft = lines.some((line) => {
      const availability = getLineAvailability(
        {
          lineId: line.id,
          quantity: asQuantity(line.quantityMilli + line.freeQuantityMilli),
          fulfilledQty: asQuantity(line.fulfilledQtyMilli),
          invoicedQty: asQuantity(line.invoicedQtyMilli),
        },
        linksByLineId.get(line.id) ?? [],
        { allowInvoiceWithoutDelivery: business.documentSettings.allowInvoiceWithoutDelivery },
      );
      return Number(availability.deliverableQty) > 0;
    });
    if (anythingLeft) dispatchable.push(order);
  }
  return dispatchable;
}

export async function listDeliveryNotes(db: DNRDatabase, businessId: string): Promise<RecentDocumentsPage> {
  return listRecentDocuments(db as never, businessId, 200, true, 'DELIVERY_NOTE');
}

export async function loadDeliveryNote(db: DNRDatabase, id: string) {
  const business = await resolveActiveBusiness(db);
  const stored = await db.documents.get(id);
  if (!stored || stored.docType !== 'DELIVERY_NOTE' || stored.businessId !== business.id) {
    throw new DeliveryNoteServiceError('NOT_FOUND', 'This delivery note is not available in the active business.');
  }
  const [model, identity, lines, links, order] = await Promise.all([
    loadDocument(db as never, id, { businessStateCode: business.stateCode, businessId: business.id }),
    loadBusinessIdentityWithImages(db as never, business.id),
    db.documentLines.where('documentId').equals(id).toArray(),
    db.documentLinks.where('fromDocumentId').equals(id).toArray(),
    stored.salesOrderId ? db.documents.get(stored.salesOrderId) : undefined,
  ]);
  const sourceLineByDeliveryLineId = new Map(
    links.filter((link) => link.relation === 'DELIVERED_AGAINST' && link.toLineId)
      .map((link) => [link.fromLineId ?? '', link.toLineId as string]),
  );
  return {
    model,
    stored,
    business: identity,
    lines,
    sourceLineByDeliveryLineId,
    order,
  };
}

